/**
 * Every page in the panel, one entry each.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * Pages used to be selected by a Vuex string that Dashboard.vue looked up in
 * a hand-written map and then, on a miss, guessed at by capitalising.
 * Guessing is how the P&L page came to render nothing: the menu said "pnl",
 * the guess produced "Pnl", and the component is called "PnL". Naming the
 * component next to its route makes that whole class of bug unreachable.
 *
 * This table lists the 77 pages that work today, and only those. P&L itself is
 * still an empty placeholder, so it gets no route and leaves the menu rather
 * than being wired up to show nothing.
 *
 * It also gives every page a real address, which is what makes the back
 * button, a bookmark, a refresh and a link to a colleague work at all.
 *
 * RULES
 * -----
 * - The route name is the old Vuex location string, unchanged. Those same
 *   strings are the values in VUE_APP_HIDDEN_PAGES, which each brand uses to
 *   hide pages it does not have, so renaming one silently breaks another
 *   brand's build.
 * - Components load lazily, grouped into one chunk per section, so a visitor
 *   downloads the section they opened instead of all eighty-odd pages. The
 *   section is the webpackChunkName comment; vite.config.mjs reads it.
 * - A page with no entry here cannot be reached. That is how the "coming
 *   soon" placeholders stay in the repository without being reachable.
 */

export default [
  {
    // The landing page. Signing in used to drop you on an empty content area
    // with only a watermark, so this is the first thing most people will see.
    path: "",
    name: "home",
    component: () => import(/* webpackChunkName: "home" */ "@/views/Home"),
  },
  {
    path: "menus",
    name: "menus",
    component: () =>
      import(/* webpackChunkName: "catalog" */ "@/views/Menus/"),
  },
  {
    path: "option-items",
    name: "option-items",
    component: () =>
      import(/* webpackChunkName: "catalog" */ "@/views/OptionItems"),
  },
  {
    path: "products",
    name: "products",
    component: () =>
      import(/* webpackChunkName: "catalog" */ "@/views/Products"),
  },
  {
    path: "featured",
    name: "featured",
    component: () =>
      import(/* webpackChunkName: "catalog" */ "@/views/Featured"),
  },
  {
    // The website's stories (pizzagarden.ca/events/blogs). ?story=<id> edits one, ?story=new
    // writes one, ?request=<id> reviews a waiting change: one page, so a link from Approvals works.
    path: "website-blog",
    name: "website-blog",
    component: () =>
      import(/* webpackChunkName: "marketing" */ "@/views/WebsiteBlog"),
  },
  {
    path: "live-orders",
    name: "live-orders",
    component: () =>
      import(/* webpackChunkName: "ops" */ "@/views/LiveOrders"),
  },
  {
    path: "group-orders",
    name: "group-orders",
    component: () =>
      import(/* webpackChunkName: "ops" */ "@/views/GroupOrders"),
  },
  {
    path: "creations",
    name: "creations",
    component: () =>
      import(/* webpackChunkName: "ops" */ "@/views/Creations"),
  },
  {
    path: "crm-acquisition",
    name: "crm-acquisition",
    component: () =>
      import(/* webpackChunkName: "crm" */ "@/views/CRM/Acquisition"),
  },
  {
    path: "crm-campaign-performance",
    name: "crm-campaign-performance",
    component: () =>
      import(/* webpackChunkName: "crm" */ "@/views/CRM/CampaignPerformance"),
  },
  {
    path: "crm-cohorts",
    name: "crm-cohorts",
    component: () =>
      import(/* webpackChunkName: "crm" */ "@/views/CRM/Cohorts"),
  },
  {
    path: "crm-lapsed",
    name: "crm-lapsed",
    component: () =>
      import(/* webpackChunkName: "crm" */ "@/views/CRM/Lapsed"),
  },
  {
    path: "ab-test-manager",
    name: "ab-test-manager",
    component: () =>
      import(/* webpackChunkName: "deal-engine" */ "@/views/DealEngine/ABTestManager"),
  },
  {
    path: "cross-sell-manager",
    name: "cross-sell-manager",
    component: () =>
      import(/* webpackChunkName: "deal-engine" */ "@/views/DealEngine/CrossSellManager"),
  },
  {
    path: "deal-analytics",
    name: "deal-analytics",
    component: () =>
      import(/* webpackChunkName: "deal-engine" */ "@/views/DealEngine/DealAnalytics"),
  },
  {
    // Same component as deal-analytics, on a route of its own. On that route
    // the server's analytics overlay hides the Vue page and draws web
    // analytics over it, so this list of offers was never actually visible.
    path: "deal-offer-performance",
    name: "deal-offer-performance",
    component: () =>
      import(/* webpackChunkName: "deal-engine" */ "@/views/DealEngine/DealAnalytics"),
  },
  {
    path: "roles",
    name: "roles",
    component: () =>
      import(/* webpackChunkName: "settings" */ "@/views/Roles"),
  },
  {
    path: "roles/edit/:key?",
    name: "role-edit",
    component: () =>
      import(/* webpackChunkName: "settings" */ "@/views/Roles/RoleForm.vue"),
  },
  {
    path: "panel-users",
    name: "panel-users",
    component: () =>
      import(/* webpackChunkName: "settings" */ "@/views/PanelUsers"),
  },
  {
    path: "deal-builder/:id?",
    name: "deal-builder",
    component: () =>
      import(/* webpackChunkName: "deal-engine" */ "@/views/DealEngine/DealBuilder"),
  },
  {
    path: "deals-dashboard",
    name: "deals-dashboard",
    component: () =>
      import(/* webpackChunkName: "deal-engine" */ "@/views/DealEngine/Dashboard"),
  },
  {
    path: "intelligence-dashboard",
    name: "intelligence-dashboard",
    component: () =>
      import(/* webpackChunkName: "deal-engine" */ "@/views/DealEngine/IntelligenceDashboard"),
  },
  {
    path: "loyalty-dashboard",
    name: "loyalty-dashboard",
    component: () =>
      import(/* webpackChunkName: "deal-engine" */ "@/views/DealEngine/LoyaltyDashboard"),
  },
  {
    path: "rule-builder",
    name: "rule-builder",
    component: () =>
      import(/* webpackChunkName: "deal-engine" */ "@/views/DealEngine/RuleBuilder"),
  },
  {
    path: "financials-avg-ticket",
    name: "financials-avg-ticket",
    component: () =>
      import(/* webpackChunkName: "financials" */ "@/views/Financials/AvgTicket"),
  },
  {
    path: "financials-discounts-voids",
    name: "financials-discounts-voids",
    component: () =>
      import(/* webpackChunkName: "financials" */ "@/views/Financials/DiscountsVoids"),
  },
  {
    path: "financials-food-cost-report",
    name: "financials-food-cost-report",
    component: () =>
      import(/* webpackChunkName: "financials" */ "@/views/Financials/FoodCostReport"),
  },
  {
    path: "financials-payment-breakdown",
    name: "financials-payment-breakdown",
    component: () =>
      import(/* webpackChunkName: "financials" */ "@/views/Financials/PaymentBreakdown"),
  },
  {
    path: "financials-royalty",
    name: "financials-royalty",
    component: () =>
      import(/* webpackChunkName: "financials" */ "@/views/Financials/RoyaltyFees.vue"),
  },
  {
    path: "automation",
    name: "automation",
    component: () =>
      import(/* webpackChunkName: "marketing" */ "@/views/Automation/"),
  },
  {
    path: "banners",
    name: "banners",
    component: () =>
      import(/* webpackChunkName: "marketing" */ "@/views/Banners"),
  },
  {
    path: "email",
    name: "email",
    component: () =>
      import(/* webpackChunkName: "marketing" */ "@/views/Email/"),
  },
  {
    path: "flash-deals",
    name: "flash-deals",
    component: () =>
      import(/* webpackChunkName: "marketing" */ "@/views/FlashDeals/"),
  },
  {
    path: "gift-cards",
    name: "gift-cards",
    component: () =>
      import(/* webpackChunkName: "marketing" */ "@/views/GiftCards/"),
  },
  {
    path: "locationAnalytics",
    name: "locationAnalytics",
    component: () =>
      import(/* webpackChunkName: "marketing" */ "@/views/LocationAnalytics/"),
  },
  {
    path: "marketing",
    name: "marketing",
    component: () =>
      import(/* webpackChunkName: "marketing" */ "@/views/Marketing/"),
  },
  {
    path: "marketing-utm-links",
    name: "marketing-utm-links",
    component: () =>
      import(/* webpackChunkName: "marketing" */ "@/views/Marketing/UtmLinks.vue"),
  },
  {
    path: "push-notifications",
    name: "push-notifications",
    component: () =>
      import(/* webpackChunkName: "marketing" */ "@/views/PushNotifications"),
  },
  {
    path: "segments",
    name: "segments",
    component: () =>
      import(/* webpackChunkName: "marketing" */ "@/views/Segments/"),
  },
  {
    path: "sms",
    name: "sms",
    component: () =>
      import(/* webpackChunkName: "marketing" */ "@/views/SMS"),
  },
  {
    path: "socialTemplates",
    name: "socialTemplates",
    component: () =>
      import(/* webpackChunkName: "marketing" */ "@/views/SocialTemplates/"),
  },
  {
    path: "analytics",
    name: "analytics",
    component: () =>
      import(/* webpackChunkName: "misc" */ "@/views/Analytics/"),
  },
  {
    path: "cases",
    name: "cases",
    component: () =>
      import(/* webpackChunkName: "misc" */ "@/views/Cases/"),
  },
  {
    path: "coupons",
    name: "coupons",
    component: () =>
      import(/* webpackChunkName: "misc" */ "@/views/Coupons"),
  },
  {
    path: "customers",
    name: "customers",
    component: () =>
      import(/* webpackChunkName: "misc" */ "@/views/Customers/"),
  },
  {
    path: "dispatch",
    name: "dispatch",
    component: () =>
      import(/* webpackChunkName: "misc" */ "@/views/Operations/Dispatch"),
  },
  {
    path: "food-cost",
    name: "food-cost",
    component: () =>
      import(/* webpackChunkName: "misc" */ "@/views/Operations/FoodCost"),
  },
  {
    path: "invoices",
    name: "invoices",
    component: () =>
      import(/* webpackChunkName: "misc" */ "@/views/Invoices"),
  },
  {
    path: "pipeline",
    name: "pipeline",
    component: () =>
      import(/* webpackChunkName: "misc" */ "@/views/Pipeline/"),
  },
  {
    path: "signups",
    name: "signups",
    component: () =>
      import(/* webpackChunkName: "misc" */ "@/views/Signups/"),
  },
  {
    path: "vendors",
    name: "vendors",
    component: () =>
      import(/* webpackChunkName: "misc" */ "@/views/Vendors"),
  },
  {
    path: "vision-ai",
    name: "vision-ai",
    component: () =>
      import(/* webpackChunkName: "misc" */ "@/views/Operations/VisionAI"),
  },
  {
    path: "voice-ai",
    name: "voice-ai",
    component: () =>
      import(/* webpackChunkName: "misc" */ "@/views/Operations/VoiceAI"),
  },
  {
    path: "store-benchmark",
    name: "store-benchmark",
    component: () =>
      import(/* webpackChunkName: "performance" */ "@/views/StorePerformance/Benchmark"),
  },
  {
    path: "store-customer-insights",
    name: "store-customer-insights",
    component: () =>
      import(/* webpackChunkName: "performance" */ "@/views/StorePerformance/CustomerInsights"),
  },
  {
    path: "store-dashboard",
    name: "store-dashboard",
    component: () =>
      import(/* webpackChunkName: "performance" */ "@/views/StorePerformance/Dashboard"),
  },
  {
    path: "store-peak-times",
    name: "store-peak-times",
    component: () =>
      import(/* webpackChunkName: "performance" */ "@/views/StorePerformance/PeakTimes"),
  },
  {
    path: "store-scorecard",
    name: "store-scorecard",
    component: () =>
      import(/* webpackChunkName: "performance" */ "@/views/StorePerformance/Scorecard"),
  },
  {
    path: "analytics-conversion",
    name: "analytics-conversion",
    component: () =>
      import(/* webpackChunkName: "reports" */ "@/views/Analytics/Conversion.vue"),
  },
  {
    path: "analytics-custom",
    name: "analytics-custom",
    component: () =>
      import(/* webpackChunkName: "reports" */ "@/views/Analytics/CustomRange"),
  },
  {
    path: "analytics-daily",
    name: "analytics-daily",
    component: () =>
      import(/* webpackChunkName: "reports" */ "@/views/Analytics/AnalyticsDaily"),
  },
  {
    path: "analytics-hourly-heatmap",
    name: "analytics-hourly-heatmap",
    component: () =>
      import(/* webpackChunkName: "reports" */ "@/views/Breakdown/HourlyHeatmap"),
  },
  {
    path: "analytics-hq-weekly",
    name: "analytics-hq-weekly",
    component: () =>
      import(/* webpackChunkName: "reports" */ "@/views/Analytics/HqWeekly.vue"),
  },
  {
    path: "analytics-monthly",
    name: "analytics-monthly",
    component: () =>
      import(/* webpackChunkName: "reports" */ "@/views/Analytics/AnalyticsMonthly"),
  },
  {
    path: "analytics-online-orders",
    name: "analytics-online-orders",
    component: () =>
      import(/* webpackChunkName: "reports" */ "@/views/Analytics/OnlineOrders"),
  },
  {
    path: "analytics-productmix",
    name: "analytics-productmix",
    component: () =>
      import(/* webpackChunkName: "reports" */ "@/views/Breakdown/AnalyticsProductMix"),
  },
  {
    path: "analytics-shift",
    name: "analytics-shift",
    component: () =>
      import(/* webpackChunkName: "reports" */ "@/views/Analytics/AnalyticsShift"),
  },
  {
    path: "analytics-weekly",
    name: "analytics-weekly",
    component: () =>
      import(/* webpackChunkName: "reports" */ "@/views/Analytics/AnalyticsWeekly"),
  },
  {
    path: "breakdown-channel-mix",
    name: "breakdown-channel-mix",
    component: () =>
      import(/* webpackChunkName: "reports" */ "@/views/Breakdown/ChannelMix"),
  },
  {
    path: "breakdown-day-part",
    name: "breakdown-day-part",
    component: () =>
      import(/* webpackChunkName: "reports" */ "@/views/Breakdown/DayPart"),
  },
  {
    path: "breakdown-delivery-heatmap",
    name: "breakdown-delivery-heatmap",
    component: () =>
      import(/* webpackChunkName: "reports" */ "@/views/Breakdown/DeliveryHeatmap"),
  },
  {
    path: "breakdown-menu-engineering",
    name: "breakdown-menu-engineering",
    component: () =>
      import(/* webpackChunkName: "reports" */ "@/views/Breakdown/MenuEngineering"),
  },
  {
    path: "breakdown-size-sales",
    name: "breakdown-size-sales",
    component: () =>
      import(/* webpackChunkName: "reports" */ "@/views/Breakdown/DailySizeSales"),
  },
  {
    path: "delivery-map",
    name: "delivery-map",
    component: () =>
      import(/* webpackChunkName: "settings" */ "@/views/BranchSettings/DeliveryMap"),
  },
  {
    path: "pos-users",
    name: "pos-users",
    component: () =>
      import(/* webpackChunkName: "settings" */ "@/views/BranchSettings/PosUsers"),
  },
  {
    path: "settings",
    name: "settings",
    component: () =>
      import(/* webpackChunkName: "settings" */ "@/views/Settings/"),
  },
  {
    path: "working-hours",
    name: "working-hours",
    component: () =>
      import(/* webpackChunkName: "settings" */ "@/views/BranchSettings/WorkingHours"),
  },
  {
    path: "stock-added-reports",
    name: "stock-added-reports",
    component: () =>
      import(/* webpackChunkName: "stock" */ "@/views/Stocks/StockAddedReports"),
  },
  {
    path: "stock-audits",
    name: "stock-audits",
    component: () =>
      import(/* webpackChunkName: "stock" */ "@/views/Stocks/StockAudits"),
  },
  {
    // Merged into Kubi's Counting (owner 2026-10-28); the old address keeps working.
    path: "stock-count",
    name: "stock-count",
    redirect: { name: "inventory-count" },
  },
  {
    // Merged into Kubi's Invoices, with Read the invoice (owner 2026-10-28); the old address keeps working.
    path: "stock-deliveries",
    name: "stock-deliveries",
    redirect: { name: "inventory-invoices" },
  },
  {
    // Merged into Kubi's Inventory Items (owner 2026-10-28); the old address keeps working.
    path: "stock-list",
    name: "stock-list",
    redirect: { name: "inventory-ingredients" },
  },
  {
    // Merged into Kubi's Dashboard (owner 2026-10-28); the old address keeps working.
    path: "stock-home",
    name: "stock-home",
    redirect: { name: "inventory-dashboard" },
  },
  {
    path: "stock-month-report",
    name: "stock-month-report",
    component: () =>
      import(/* webpackChunkName: "stock" */ "@/views/Inventory/MonthReport.vue"),
  },
  {
    // Merged into Kubi's Stock Health (owner 2026-10-28); the old address keeps working.
    path: "stock-onhand",
    name: "stock-onhand",
    redirect: { name: "inventory-stock-health" },
  },
  {
    // Merged into Kubi's Stock Health, All stores (owner 2026-10-28); the old address keeps working.
    path: "stock-overview",
    name: "stock-overview",
    redirect: { name: "inventory-stock-health" },
  },
  {
    path: "stock-reducted-reports",
    name: "stock-reducted-reports",
    component: () =>
      import(/* webpackChunkName: "stock" */ "@/views/Stocks/StockReductedReports"),
  },
  {
    // Merged into Kubi's Purchasing (suggested orders, owner 2026-10-28); the old address keeps working.
    path: "stock-reorder",
    name: "stock-reorder",
    redirect: { name: "inventory-purchasing" },
  },
  {
    // Merged into Kubi's Waste Log (owner 2026-10-28); the old address keeps working.
    path: "stock-waste",
    name: "stock-waste",
    redirect: { name: "inventory-waste-log" },
  },
  {
    path: "stocks",
    name: "stocks",
    component: () =>
      import(/* webpackChunkName: "stock" */ "@/views/Stocks"),
  },
  // ── Inventory: Kubi's Invorest on Connect (spec: connect_inventory_a_2026-10-28/spec/00_master.md) ──
  {
    path: "inventory-ingredients",
    name: "inventory-ingredients",
    component: () =>
      import(/* webpackChunkName: "inventory" */ "@/views/Inventory/Ingredients.vue"),
  },
  {
    path: "inventory-count",
    name: "inventory-count",
    component: () =>
      import(/* webpackChunkName: "inventory" */ "@/views/Inventory/Counting.vue"),
  },
  {
    path: "inventory-waste-log",
    name: "inventory-waste-log",
    component: () =>
      import(/* webpackChunkName: "inventory" */ "@/views/Inventory/WasteLog.vue"),
  },
  {
    path: "inventory-variance",
    name: "inventory-variance",
    component: () =>
      import(/* webpackChunkName: "inventory" */ "@/views/Inventory/Variance.vue"),
  },
  {
    path: "inventory-stock-health",
    name: "inventory-stock-health",
    component: () =>
      import(/* webpackChunkName: "inventory" */ "@/views/Inventory/StockHealth.vue"),
  },
  {
    path: "inventory-alerts",
    name: "inventory-alerts",
    component: () =>
      import(/* webpackChunkName: "inventory" */ "@/views/Inventory/Alerts.vue"),
  },
  {
    path: "inventory-purchasing",
    name: "inventory-purchasing",
    component: () =>
      import(/* webpackChunkName: "inventory" */ "@/views/Inventory/Purchasing.vue"),
  },
  {
    path: "inventory-draft-pos",
    name: "inventory-draft-pos",
    component: () =>
      import(/* webpackChunkName: "inventory" */ "@/views/Inventory/DraftPOs.vue"),
  },
  {
    path: "inventory-pos/:id",
    name: "inventory-po-editor",
    component: () =>
      import(/* webpackChunkName: "inventory" */ "@/views/Inventory/POEditor.vue"),
  },
  {
    path: "inventory-receiving",
    name: "inventory-receiving",
    component: () =>
      import(/* webpackChunkName: "inventory" */ "@/views/Inventory/Receiving.vue"),
  },
  {
    path: "inventory-invoices",
    name: "inventory-invoices",
    component: () =>
      import(/* webpackChunkName: "inventory" */ "@/views/Inventory/Invoices.vue"),
  },
  {
    path: "inventory-price-drift",
    name: "inventory-price-drift",
    component: () =>
      import(/* webpackChunkName: "inventory" */ "@/views/Inventory/PriceDrift.vue"),
  },
  {
    path: "inventory-transfers",
    name: "inventory-transfers",
    component: () =>
      import(/* webpackChunkName: "inventory" */ "@/views/Inventory/Transfers.vue"),
  },
  {
    path: "inventory-audit",
    name: "inventory-audit",
    component: () =>
      import(/* webpackChunkName: "inventory" */ "@/views/Inventory/AuditTrail.vue"),
  },
  {
    path: "inventory-dashboard",
    name: "inventory-dashboard",
    component: () =>
      import(/* webpackChunkName: "inventory" */ "@/views/Inventory/Dashboard.vue"),
  },
  {
    path: "inventory-vendors",
    name: "inventory-vendors",
    component: () =>
      import(/* webpackChunkName: "inventory" */ "@/views/Inventory/Vendors.vue"),
  },
  {
    path: "inventory-products",
    name: "inventory-products",
    component: () =>
      import(/* webpackChunkName: "inventory" */ "@/views/Inventory/Products.vue"),
  },
  {
    path: "inventory-toppings",
    name: "inventory-toppings",
    component: () =>
      import(/* webpackChunkName: "inventory" */ "@/views/Inventory/Toppings.vue"),
  },
  {
    path: "inventory-preps",
    name: "inventory-preps",
    component: () =>
      import(/* webpackChunkName: "inventory" */ "@/views/Inventory/Preps.vue"),
  },
  {
    path: "inventory-packaging",
    name: "inventory-packaging",
    component: () =>
      import(/* webpackChunkName: "inventory" */ "@/views/Inventory/Packaging.vue"),
  },
  {
    path: "inventory-cogs",
    name: "inventory-cogs",
    component: () =>
      import(/* webpackChunkName: "inventory" */ "@/views/Inventory/Cogs.vue"),
  },
  {
    path: "inventory-cogs-variance",
    name: "inventory-cogs-variance",
    component: () =>
      import(/* webpackChunkName: "inventory" */ "@/views/Inventory/CogsVariance.vue"),
  },
  {
    path: "inventory-menu-engineering",
    name: "inventory-menu-engineering",
    component: () =>
      import(/* webpackChunkName: "inventory" */ "@/views/Inventory/MenuEngineering.vue"),
  },
  {
    path: "inventory-dough",
    name: "inventory-dough",
    component: () =>
      import(/* webpackChunkName: "inventory" */ "@/views/Inventory/Dough.vue"),
  },
  {
    path: "inventory-users",
    name: "inventory-users",
    component: () =>
      import(/* webpackChunkName: "inventory" */ "@/views/Inventory/Users.vue"),
  },
  {
    path: "inventory-first-list",
    name: "inventory-first-list",
    component: () =>
      import(/* webpackChunkName: "inventory" */ "@/views/Inventory/FirstList.vue"),
  },
];
